import { ChangeDetectionStrategy, Component, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Remittance } from '../../../../../models/remittances/remittance';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { ContributionSummary } from '../../../../../models/remittances/contribution-summary';
import { PortalService } from '../../../../../services/portal/portal.service';
import { RemittanceStatusIdEnum } from '../../../../../models/enum/remittance-status-id.enum';

@Component({
  selector: 'app-remittance-item-details',
  templateUrl: './remittance-item-details.component.html',
  styleUrls: ['./remittance-item-details.component.scss'],
  providers: [CurrencyPipe, DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RemittanceItemDetailsComponent implements OnChanges {
  @Input() remittance!: Remittance;
  @Input() contributionSummary!: ContributionSummary;
  @Input() showEmployerInfo = true;
  @Input() showSource = true;
  @Input() isLoading = false;

  private currencyPipe = inject(CurrencyPipe);
  private datePipe = inject(DatePipe);
  private portalService = inject(PortalService);

  private _remittance = new BehaviorSubject<Remittance | null>(null);
  public remittance$ = this._remittance.pipe(filter((remittance): remittance is Remittance => !!remittance));

  private _contributionSummary = new BehaviorSubject<ContributionSummary | null>(null);
  public contributionSummary$ = this._contributionSummary.pipe(
    filter((contributionSummary): contributionSummary is ContributionSummary => !!contributionSummary)
  );

  private _showEmployerInfo = new BehaviorSubject<boolean>(this.showEmployerInfo);
  public showEmployerInfo$ = this._showEmployerInfo as BehaviorSubject<boolean>;

  private _showSource = new BehaviorSubject<boolean>(this.showSource);
  public showSource$ = this._showSource as BehaviorSubject<boolean>;

  public isEmployerPortal$ = this.portalService.isEmployer$;

  public remittanceDetails$ = combineLatest([
    this.portalService.isEmployer$,
    this.remittance$.pipe(startWith(null)),
    this.contributionSummary$.pipe(startWith(null)),
    this.showEmployerInfo$,
    this.showSource$
  ]).pipe(
    map(([isEmployerPortal, remittance, contributionSummary, showEmployerInfo, showSource]) => {
      const totalAmount = contributionSummary?.totalAmount ?? remittance?.totalRemittanceAmount ?? 0;
      const numberOfRecords = contributionSummary?.totalEntryCount ?? remittance?.contributionEntryCount ?? 0;
      const numberOfMembers = contributionSummary?.totalContributionCount ?? remittance?.contributionCount ?? 0;
      return [
        ...(showEmployerInfo
          ? [
              { label: $localize`Employer Name`, value: remittance?.employerName },
              {
                label: $localize`Employer Number`,
                value: remittance?.employerId.toString(10).padStart(8, '0'),
                url: `/employers/${remittance?.employerId}`
              }
            ]
          : []),
        {
          label:
            isEmployerPortal || remittance?.statusId === RemittanceStatusIdEnum.Pending
              ? $localize`Date Submitted`
              : $localize`Date Received`,
          value: remittance?.submittedDate ? this.datePipe.transform(remittance?.submittedDate, 'MMMM dd, yyyy') : '-'
        },
        {
          label: $localize`Pay Period End Date`,
          value: remittance?.payPeriodEndDate
            ? this.datePipe.transform(remittance?.payPeriodEndDate, 'MMMM dd, yyyy')
            : '-'
        },
        ...(showSource ? [{ label: $localize`Source`, value: remittance?.source?.name ?? '-' }] : []),
        {
          label: $localize`Total Remittance Amount`,
          value: this.currencyPipe.transform(totalAmount)
        },
        {
          label: isEmployerPortal ? $localize`Number of Employees` : $localize`Number of Members`,
          value: numberOfMembers
        },
        { label: $localize`Number of Records`, value: numberOfRecords },
        {
          label: $localize`Taxation Year`,
          value: this.datePipe.transform(remittance?.taxationYear, 'yyyy') ?? '-'
        }
      ];
    })
  );

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.remittance) {
      this._remittance.next(this.remittance);
    }
    if (changes.contributionSummary) {
      this._contributionSummary.next(this.contributionSummary);
    }
    if (changes.showEmployerInfo) {
      this._showEmployerInfo.next(this.showEmployerInfo);
    }
    if (changes.showSource) {
      this._showSource.next(this.showSource);
    }
  }

  protected readonly RemittanceStatusIdEnum = RemittanceStatusIdEnum;
}
